import type { BookingLocale, BookingMessages } from "@/i18n/types";
import { formatCurrency, grossAmountFromNet } from "@/lib/money";
import type { PriceQuote, PriceQuoteLine } from "@/types";

const NIGHT_MULTIPLIED_RULE_TYPES = new Set([
  "pitch_category_night",
  "adult_night",
  "child_night",
  "dog_night",
  "electricity_night",
]);

function formatQuantity(value: string): string {
  const num = Number(value);
  if (!Number.isFinite(num)) return value;
  if (Number.isInteger(num)) return String(num);
  return num.toLocaleString("de-DE", { maximumFractionDigits: 2 });
}

export function formatPriceLineCalculation(
  line: PriceQuoteLine,
  quote: PriceQuote,
  messages: BookingMessages,
  locale: BookingLocale,
): string {
  const grossUnit = grossAmountFromNet(line.unit_price, line.tax_rate_percent);
  if (grossUnit == null) return "";

  const unitFormatted = formatCurrency(grossUnit, quote.currency, locale);
  if (line.billing_nights === 0) {
    const expected = Number(line.quantity) * grossUnit;
    const actual = Number(line.line_total);
    if (Number.isFinite(expected) && Number.isFinite(actual) && Math.abs(actual - expected) < 0.05) {
      return messages.price.lineUnitFormula(formatQuantity(line.quantity), unitFormatted);
    }
    return "";
  }
  const nights = line.billing_nights ?? quote.nights;
  const nightLabel = nights === 1 ? messages.common.night : messages.common.nights;
  const qty = formatQuantity(line.quantity);

  if (NIGHT_MULTIPLIED_RULE_TYPES.has(line.rule_type) && nights > 0) {
    return messages.price.lineNightFormula(qty, nights, nightLabel, unitFormatted);
  }

  return messages.price.lineUnitFormula(qty, unitFormatted);
}
